import React from 'react';
import { Grid } from '@mui/material';
import { Note, SignedValue, Tag } from '@/components/ui';
import { formatCurrency, formatDate, formatNumber, formatPercentage } from '@/utils/formatters';
import {
    SectionCard,
    StaticTable,
    SummaryGrid,
    SummaryItem,
    ReturnCell,
    BenchmarkCell,
    ShareCell,
    maxShare,
    LONG_LIST_MAX_HEIGHT,
} from './ReportSections';
import styles from './ClientPerformance.module.css';
import { ColumnDef } from '../utils/DataTable';

// Payload shape returned by the backend for IFSC (GIFT City) clients
// (report_type === 'IFSC_AIF'). Series-accounted: one NAV series per
// subscription, capital flows in the fund's own currency.

export interface IFSCSeriesRow {
    series_code: string;
    class_name: string;
    nav_date: string;
    nav: number;
    units: number;
    valuation: number;
    xirr: number | null;
}

export interface IFSCTransactionRow {
    date: string;
    transaction_type: string;
    allotment_nav: number | null;
    units_allotted: number | null;
    amount: number;
}

export interface IFSCAllocationRow {
    sector_name: string;
    percentage: number;
}

export interface IFSCHoldingRow {
    symbol_code: string;
    sector_name: string;
    asset_class: string;
    percentage: number;
}

export interface IFSCReportData {
    report_type?: string;
    holding_date: string;
    nav_date_snapped?: boolean;
    inception_date: string;
    // Always empty today; present so the shared warnings banner can read the
    // payload without casting.
    warnings?: Array<{ from_date: string; message: string }>;
    // Fund currency code from the backend (from the contribution ledger) — never assumed.
    currency?: string | null;
    series?: IFSCSeriesRow[];
    series_total?: { valuation: number; xirr: number | null };
    transactions?: IFSCTransactionRow[];
    total_contribution?: number;
    total_withdrawal?: number;
    net_capital_in_out?: number;
    total_portfolio_value?: number;
    total_gain?: number;
    holdings_as_of?: string | null;
    sector_allocation?: IFSCAllocationRow[];
    holdings?: IFSCHoldingRow[];
    // Portfolio-level period returns (e.g. "1_month", "3_months", "6_months"),
    // keyed the same way as the standard PMS/AIF report. A period key absent
    // from the map (or null-valued) means the fund's NAV history doesn't
    // reach back that far yet.
    twrr?: Record<string, number | null>;
    xirr?: Record<string, number | null>;
    // Benchmark (BSE500, converted to USD) figures, same shape/period keys as
    // twrr/xirr — same BenchmarkCalculator engine the standard report uses for
    // BSE500, just USD-converted since IFSC portfolios/flows are USD-denominated.
    benchmark?: Record<string, number | null>;
    benchmark_irr?: Record<string, number | null>;
}

interface PeriodRow { period: string; value: number | null; benchmark: number | null; }

const prettifyPeriod = (period: string) =>
    period.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const periodTwrrColumns: ColumnDef<PeriodRow>[] = [
    { key: 'period', header: 'Period', render: row => prettifyPeriod(row.period) },
    {
        key: 'value',
        header: 'TWRR',
        align: 'right',
        tooltip: 'Valuation-weighted average of each series’ own NAV-per-unit return since its nearest available snapshot at the start of the period. A series with no snapshot that far back is excluded.',
        render: row => <ReturnCell value={row.value} />,
    },
    { key: 'benchmark', header: 'BSE500 (USD)', align: 'right', render: row => <BenchmarkCell value={row.benchmark} /> },
];

const periodXirrColumns: ColumnDef<PeriodRow>[] = [
    { key: 'period', header: 'Period', render: row => prettifyPeriod(row.period) },
    { key: 'value', header: 'XIRR', align: 'right', render: row => <ReturnCell value={row.value} /> },
    { key: 'benchmark', header: 'BSE500 (USD)', align: 'right', render: row => <BenchmarkCell value={row.benchmark} /> },
];

// The account-summary total is rendered as a synthetic last row, bolded per cell.
type SeriesTableRow = IFSCSeriesRow & { is_total?: boolean };

const bold = (row: SeriesTableRow, content: React.ReactNode) =>
    row.is_total ? <strong>{content}</strong> : content;

const buildSectorColumns = (max: number): ColumnDef<IFSCAllocationRow>[] => [
    { key: 'sector_name', header: 'Sector', render: row => row.sector_name },
    { key: 'percentage', header: '%', align: 'right', render: row => <ShareCell value={row.percentage} max={max} /> },
];

const buildHoldingsColumns = (max: number): ColumnDef<IFSCHoldingRow>[] => [
    { key: 'symbol_code', header: 'Symbol', render: row => <span className={styles.symbol}>{row.symbol_code}</span> },
    { key: 'sector_name', header: 'Sector', render: row => row.sector_name },
    { key: 'asset_class', header: 'Asset Class', render: row => <Tag>{row.asset_class}</Tag> },
    { key: 'percentage', header: '%', align: 'right', render: row => <ShareCell value={row.percentage} max={max} /> },
];

const IFSCPerformanceReport: React.FC<{ data: IFSCReportData }> = ({ data }) => {
    const currency = data.currency ?? undefined;

    const seriesRows: SeriesTableRow[] = [
        ...(data.series ?? []),
        {
            series_code: 'Total',
            class_name: '',
            nav_date: '',
            nav: 0,
            units: 0,
            valuation: data.series_total?.valuation ?? 0,
            xirr: data.series_total?.xirr ?? null,
            is_total: true,
        },
    ];

    const seriesColumns: ColumnDef<SeriesTableRow>[] = [
        { key: 'series_code', header: 'Series', render: row => bold(row, row.series_code) },
        { key: 'class_name', header: 'Class', render: row => row.class_name },
        { key: 'nav_date', header: 'NAV Date', render: row => row.nav_date ? formatDate(row.nav_date) : '' },
        { key: 'nav', header: 'NAV', align: 'right', render: row => row.is_total ? '' : formatNumber(row.nav, 4) },
        { key: 'units', header: 'Units', align: 'right', render: row => row.is_total ? '' : formatNumber(row.units, 4) },
        { key: 'valuation', header: `Valuation${currency ? ` (${currency})` : ''}`, align: 'right', render: row => bold(row, formatCurrency(row.valuation, currency)) },
        { key: 'xirr', header: 'XIRR (%)', align: 'right', render: row => bold(row, <SignedValue value={row.xirr} format={formatPercentage} />) },
    ];

    const transactionColumns: ColumnDef<IFSCTransactionRow>[] = [
        { key: 'date', header: 'Date', render: row => formatDate(row.date) },
        { key: 'transaction_type', header: 'Transaction', render: row => <Tag>{row.transaction_type}</Tag> },
        { key: 'allotment_nav', header: 'Allotment NAV', align: 'right', render: row => row.allotment_nav != null ? formatNumber(row.allotment_nav, 4) : '—' },
        { key: 'units_allotted', header: 'Units Allotted', align: 'right', render: row => row.units_allotted != null ? formatNumber(row.units_allotted, 4) : '—' },
        { key: 'amount', header: `Amount${currency ? ` (${currency})` : ''}`, align: 'right', render: row => formatCurrency(row.amount, currency) },
    ];

    // Current Valuation leads as the headline figure; the rest pair up beneath it.
    const summaryItems: SummaryItem[] = [
        { label: 'Current Valuation', value: formatCurrency(data.total_portfolio_value, currency), hero: true },
        { label: 'Inception Date', value: formatDate(data.inception_date) },
        {
            label: 'Total Gain',
            value: (
                <SignedValue
                    value={data.total_gain}
                    format={value => formatCurrency(value, currency)}
                    empty={formatCurrency(data.total_gain, currency)}
                />
            ),
        },
        { label: 'Total Contribution', value: formatCurrency(data.total_contribution, currency) },
        { label: 'Total Withdrawal', value: formatCurrency(data.total_withdrawal, currency) },
        { label: 'Net Capital In (+) / Out (-)', value: formatCurrency(data.net_capital_in_out, currency) },
    ];

    const sectorColumns = buildSectorColumns(maxShare(data.sector_allocation));
    const holdingsColumns = buildHoldingsColumns(maxShare(data.holdings));

    const twrrRows: PeriodRow[] = Object.entries(data.twrr ?? {}).map(([period, value]) => ({
        period, value, benchmark: data.benchmark?.[period] ?? null,
    }));
    const xirrRows: PeriodRow[] = Object.entries(data.xirr ?? {}).map(([period, value]) => ({
        period, value, benchmark: data.benchmark_irr?.[period] ?? null,
    }));

    return (
        <>
            <Note tone="info">
                NAV for this fund is published periodically per series
                {data.nav_date_snapped
                    ? `; the report reflects the most recent available NAV date (${formatDate(data.holding_date)})`
                    : ''}
                .{currency ? ` All amounts are in ${currency} except units.` : ''}
            </Note>

            <Grid container spacing={1.5}>
                <Grid size={12}>
                    <SectionCard title="Account Summary" meta={`${data.series?.length ?? 0} series`}>
                        <StaticTable
                            data={seriesRows}
                            columns={seriesColumns}
                            rowKey={(row) => row.is_total ? 'total' : row.series_code}
                        />
                    </SectionCard>
                </Grid>
            </Grid>

            <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <SectionCard title="Transaction Details">
                        <StaticTable
                            data={data.transactions ?? []}
                            columns={transactionColumns}
                            rowKey={(row, index) => `${row.date}-${row.transaction_type}-${index}`}
                        />
                    </SectionCard>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                    <SectionCard title="Performance Summary">
                        <SummaryGrid items={summaryItems} />
                    </SectionCard>
                </Grid>
            </Grid>

            {twrrRows.length > 0 || xirrRows.length > 0 ? (
                <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SectionCard title="TWRR Performance">
                            <StaticTable data={twrrRows} columns={periodTwrrColumns} rowKey={(row) => row.period} />
                        </SectionCard>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <SectionCard title="XIRR">
                            <StaticTable data={xirrRows} columns={periodXirrColumns} rowKey={(row) => row.period} />
                        </SectionCard>
                    </Grid>
                </Grid>
            ) : null}

            {(data.sector_allocation?.length ?? 0) > 0 || (data.holdings?.length ?? 0) > 0 ? (
                <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <SectionCard
                            title="Sector Allocation"
                            subtitle={`Fund-level (pooled) allocation${data.holdings_as_of ? ` as of ${formatDate(data.holdings_as_of)}` : ''}.`}
                        >
                            <StaticTable data={data.sector_allocation ?? []} columns={sectorColumns} rowKey={(row) => row.sector_name} maxBodyHeight={LONG_LIST_MAX_HEIGHT} />
                        </SectionCard>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                        <SectionCard
                            title="Portfolio Holdings"
                            subtitle={`Fund-level (pooled) positions${data.holdings_as_of ? ` as of ${formatDate(data.holdings_as_of)}` : ''}; percentages of fund total.`}
                        >
                            <StaticTable data={data.holdings ?? []} columns={holdingsColumns} rowKey={(row) => `${row.symbol_code}-${row.asset_class}`} maxBodyHeight={LONG_LIST_MAX_HEIGHT} />
                        </SectionCard>
                    </Grid>
                </Grid>
            ) : null}
        </>
    );
};

export default IFSCPerformanceReport;
